/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';
import { Link } from 'react-router';

import { compose } from 'recompose';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';
import mapProps from 'js/lib/mapProps';

import { Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import ItemNavigationLabel, {
  getItemNavigationLabel,
} from 'bundles/course-frame/components/course-outline/ItemNavigationLabel';
import ProgressIcon, { progressIconHoverStyles } from 'bundles/course-frame/components/course-outline/ProgressIcon';
import StatusIcon, { getStatusIconLabel } from 'bundles/course-frame/components/course-outline/StatusIcon';
import {
  computedItemToCourseItem,
  useCourseFrameTrackingData,
} from 'bundles/course-frame/components/providers/CourseFrameEventingProvider';
import { useCourseFrameContext } from 'bundles/course-frame/components/providers/CourseFrameProvider';
import { getItemState } from 'bundles/course-frame/utils/progressIconUtils';
import { incrementM3NavigationCount } from 'bundles/course-frame/utils/viewCountUtils';
import withIsScheduleIncentivesEnabled from 'bundles/course-home/page-course-week/utils/withIsScheduleIncentivesEnabled';
import { type PropsFromWithCurrentSession } from 'bundles/course-sessions/utils/withCurrentSession';
import type { PropsWithSessionLabel } from 'bundles/course-sessions/utils/withSessionLabel';
import withSessionLabel from 'bundles/course-sessions/utils/withSessionLabel';
import withSessionsV2EnrollmentEnabled, {
  type PropsFromWithSessionsV2EnrollmentEnabled,
} from 'bundles/course-sessions/utils/withSessionsV2EnrollmentEnabled';
import { useIsFreemiumLocked } from 'bundles/enroll-common/utils/freemiumModelUtils';
import CourseStore from 'bundles/in-course-stores/stores/CourseStore';
import type { Item } from 'bundles/learner-progress/types/Item';
import { getFormattedLockMessage, getFormattedTitlePrefix } from 'bundles/learner-progress/utils/Item';
import { canShowDistinctAvailabilityReviewDates } from 'bundles/learner-progress/utils/canShowDistinctAvailabilityReviewDates';
import { useCourseAiGradingContext } from 'bundles/peer/components/ai-grading/CourseAiEligibilityProvider';

import _t from 'i18n!nls/course-frame';

export const PROGRESS_ICON_PADDING = 8;

const styles = {
  container: css`
    display: flex;
    border-left: 4px solid transparent;
    background-color: var(--cds-color-neutral-background-primary);

    &:hover {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
    }
    ${progressIconHoverStyles}

    &:focus {
      text-decoration: none;
    }

    /* Ensure we can position the pseudo-element for the custom focus ring */
    position: relative;

    &:focus-visible {
      /* Remove the default outline so we can apply a custom one */
      outline: none;
    }

    /* Custom focus ring with rounded corners to match CDS styles */
    &:focus-visible::after {
      content: '';
      position: absolute;
      top: 0;
      left: -4px; /* compensate for the border-left in the container */
      right: 0;
      bottom: 0;
      pointer-events: none;
      border: 1px solid var(--cds-color-interactive-stroke-primary-focus);
      border-radius: var(--cds-border-radius-50);
    }
  `,
  freemiumLockedModule: css`
    background-color: var(--cds-color-emphasis-neutral-background-xweak);

    /* icon color */
    .rc-cf-progress-icon {
      /* fixed background color for the freemium locked module icon */
      --cf-progress-icon-not-selected-color: var(--cds-color-grey-200) !important;
    }

    &:hover {
      background-color: var(--cds-color-emphasis-neutral-background-weak);
    }
  `,
  content: css`
    display: flex;
    width: 100%;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    gap: 12px;
  `,
  icon: css`
    align-self: center;
    height: 20px;
  `,
  selected: css`
    border-left: 4px solid var(--cds-color-interactive-primary);
    background-color: var(--cds-color-interactive-background-primary-hover-weak);

    &:hover {
      border-color: var(--cds-color-interactive-primary-hover);
    }
  `,
  selectedPhase3: css`
    background-color: var(--cds-color-interactive-background-primary-hover-weak);
  `,
  freemiumSelected: css`
    background-color: var(--cds-color-emphasis-neutral-background-weak);
  `,
  highlightedTitle: css`
    font-weight: 600;
  `,
};

type PropsFromCaller = {
  computedItem: Item;
  highlighted?: boolean;
};

export type PropsFromCourseStore = {
  isGuidedProject: boolean;
  partnerIds: number[] | undefined;
  isVerifiedSkillsCourse: boolean;
};

type PropsFromHocs = PropsWithSessionLabel<PropsFromCaller> &
  PropsFromWithCurrentSession &
  PropsFromWithSessionsV2EnrollmentEnabled & {
    isScheduleIncentivesEnabled: boolean;
  };

export type PropsToComponent = PropsFromCaller & PropsFromHocs;

/**
 * This is Work-In-Progress component to replace the existing NavSingleItemDisplay.
 * @see https://github.com/webedx-spark/web/blob/main/static/bundles/learner-progress/components/item/nav/private/NavSingleItemDisplay.tsx
 */
export const NavSingleItemDisplayV2 = (props: PropsToComponent) => {
  const track = useTracker();
  const trackingDataV3 = useCourseFrameTrackingData();
  const { setIsMobileNavOpen, isPhase3Enabled } = useCourseFrameContext();
  const { computedItem, highlighted, sessionsV2EnrollmentEnabled, sessionLabel, isScheduleIncentivesEnabled } = props;
  const { getItemAiGradingEligibility } = useCourseAiGradingContext();
  const isAiGradingEligible = getItemAiGradingEligibility(computedItem.id);

  const { isGuidedProject, partnerIds, isVerifiedSkillsCourse } = useFluxibleStore(
    CourseStore,
    (courseStore): PropsFromCourseStore => ({
      isGuidedProject: courseStore.isGuidedProject(),
      partnerIds: courseStore.getMetadata()?.get('partnerIds'),
      isVerifiedSkillsCourse: courseStore.isVerifiedSkillsCourse(),
    })
  );

  const isFreemiumLocked = useIsFreemiumLocked({ moduleNumber: computedItem.weekNumber });

  // Course Frame: This is duplicated from static/bundles/learner-progress/components/item/locking/LockedTooltip.tsx
  const showDistinctAvailabilityReviewDates = canShowDistinctAvailabilityReviewDates(partnerIds);
  const { itemLockedStatus, itemLockedReasonCode, itemLockSummary, deadline } = computedItem;
  const lockMessage = getFormattedLockMessage({
    sessionsV2Enabled: sessionsV2EnrollmentEnabled,
    sessionLabel,
    itemLockedStatus,
    itemLockedReasonCode,
    itemLockSummary,
    isScheduleIncentivesEnabled,
    deadline,
    showDistinctAvailabilityReviewDates,
    isFreemiumLocked,
  });

  const isLocked = !!lockMessage;

  const formattedTitlePrefix = getFormattedTitlePrefix(
    computedItem,
    isGuidedProject,
    isAiGradingEligible,
    isVerifiedSkillsCourse
  );

  const itemNavigationLabel = useMemo(
    () => getItemNavigationLabel({ computedItem, isFreemiumLocked, isLocked, isPhase3Enabled }),
    [computedItem, isFreemiumLocked, isLocked, isPhase3Enabled]
  );

  const iconLabelForLink = getStatusIconLabel(computedItem);

  const highlightedLabel = highlighted ? _t('selected link') : '';
  const ariaPieces: string[] = [
    highlightedLabel,
    formattedTitlePrefix,
    computedItem.name,
    iconLabelForLink,
    itemNavigationLabel,
  ].filter(
    // We support empty strings in some of these types, but we want empty strings excluded from the aria-label.
    Boolean
  );
  const ariaLabel = ariaPieces.join(', ');

  const handleClick = () => {
    track('click_button', {
      button: { name: 'course_frame_open_outline_item' },
      ...trackingDataV3,
      courseItem: computedItemToCourseItem(computedItem),
    });
    setIsMobileNavOpen(false);
    if (isPhase3Enabled) {
      incrementM3NavigationCount();
    }
  };

  return (
    <Link
      to={computedItem.resourcePath}
      onClick={handleClick}
      css={[
        styles.container,
        highlighted && (isPhase3Enabled ? styles.selectedPhase3 : styles.selected),
        isFreemiumLocked && styles.freemiumLockedModule,
        highlighted && isFreemiumLocked && styles.freemiumSelected,
      ]}
      aria-label={ariaLabel}
    >
      <div css={styles.content} className="outline-single-item-content-wrapper">
        {isPhase3Enabled ? (
          <ProgressIcon
            itemState={getItemState(computedItem)}
            isSelected={highlighted}
            tooltipMessage={lockMessage}
            containerPaddingY={PROGRESS_ICON_PADDING}
          />
        ) : (
          <div css={styles.icon}>
            <StatusIcon computedItem={computedItem} tooltipMessage={lockMessage} />
          </div>
        )}

        <div>
          <Typography2
            variant="bodySecondary"
            component="div"
            color="body"
            css={highlighted && styles.highlightedTitle}
          >
            {computedItem.name}
          </Typography2>
          <Typography2 variant="bodySecondary" component="div" color="emphasis.neutral">
            {formattedTitlePrefix}
            <ItemNavigationLabel
              computedItem={computedItem}
              isFreemiumLocked={isFreemiumLocked}
              isLocked={isLocked}
              isPhase3Enabled={isPhase3Enabled}
            />
          </Typography2>
        </div>
      </div>
    </Link>
  );
};

// Course Frame: This HOC chain is duplicated from static/bundles/learner-progress/components/item/locking/LockedTooltip.tsx
export default compose<PropsToComponent, PropsFromCaller>(
  withSessionsV2EnrollmentEnabled((propsFromCaller) => propsFromCaller.computedItem.courseId),
  mapProps((propsFromCaller: PropsFromCaller) => ({
    courseId: propsFromCaller.computedItem.courseId,
  })),
  // TODO(riwong): Refactor withSessionLabel to the course-level and also migrate from HOC to hook.
  withSessionLabel,
  withIsScheduleIncentivesEnabled
)(NavSingleItemDisplayV2);
